import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import type { Database } from "@/integrations/supabase/types";
import {
  setProofMediaScope,
  invalidateAllProofMedia,
  getProofMediaProjectRef,
} from "@/lib/proofMedia";

type Tenant = Database['public']['Tables']['tenants']['Row'];
type Membership = Database['public']['Tables']['memberships']['Row'] & {
  tenant: Tenant;
};

interface TenantContextType {
  currentTenant: Tenant | null;
  userMemberships: Membership[];
  userRole: string | null;
  loading: boolean;
  allMembershipsSuspended: boolean;
  hasOnlyPendingMemberships: boolean;
  isOrphanNeedingOnboarding: boolean;
  isSuperAdmin: boolean;
  refreshTenantData: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  currentTenant: null,
  userMemberships: [],
  userRole: null,
  loading: true,
  allMembershipsSuspended: false,
  hasOnlyPendingMemberships: false,
  isOrphanNeedingOnboarding: false,
  isSuperAdmin: false,
  refreshTenantData: async () => {},
});

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { session, user } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [userMemberships, setUserMemberships] = useState<Membership[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [allMembershipsSuspended, setAllMembershipsSuspended] = useState(false);
  const [hasOnlyPendingMemberships, setHasOnlyPendingMemberships] = useState(false);
  const [isOrphanNeedingOnboarding, setIsOrphanNeedingOnboarding] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Get stored tenant from login or URL parameter
  const getSelectedTenant = (): string | null => {
    // Check for stored tenant from login
    const storedTenantSlug = localStorage.getItem('selectedTenantSlug');
    if (storedTenantSlug) {
      return storedTenantSlug;
    }
    
    // Fallback to URL parameter for development
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tenant');
  };

  // Check if user has active invitation (to prevent race condition)
  const hasActiveInvitation = async (userId: string): Promise<boolean> => {
    const { data: invitations } = await supabase
      .from('user_invitations')
      .select('id, expires_at')
      .eq('user_id', userId)
      .is('used_at', null);
    
    if (!invitations || invitations.length === 0) return false;
    
    // Check if any invitation is still valid (not expired)
    const now = new Date();
    return invitations.some(inv => {
      if (!inv.expires_at) return true; // No expiry = always valid
      return new Date(inv.expires_at) > now;
    });
  };

  const loadTenantData = async () => {
    if (!user) {
      // Explicit sign-out / no-session path: clear everything so a real
      // sign-out is never masked by preserved-on-error state below.
      setCurrentTenant(null);
      setUserMemberships([]);
      setUserRole(null);
      setAllMembershipsSuspended(false);
      setHasOnlyPendingMemberships(false);
      setIsOrphanNeedingOnboarding(false);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    // Enter loading for this run so guards show a spinner instead of a
    // transiently-null tenant while we re-fetch.
    setLoading(true);

    try {
      // A Super Admin is a platform-level identity, not a tenant identity.
      // Resolve this before memberships so a historical/accidental membership
      // cannot send a Super Admin into a tenant dashboard.
      const { data: adminRow, error: adminError } = await supabase
        .from("app_users")
        .select("is_super_admin")
        .eq("id", user.id)
        .maybeSingle();

      if (adminError) {
        console.error("[TenantContext] Error checking Super Admin status:", adminError);
        throw adminError;
      }

      if (adminRow?.is_super_admin) {
        setIsSuperAdmin(true);
        setUserMemberships([]);
        setCurrentTenant(null);
        setUserRole(null);
        setIsOrphanNeedingOnboarding(false);
        setHasOnlyPendingMemberships(false);
        setAllMembershipsSuspended(false);
        localStorage.removeItem("selectedTenantId");
        localStorage.removeItem("selectedTenantSlug");
        setLoading(false);
        return;
      }
      setIsSuperAdmin(false);

      // Fetch user memberships
      const { data: memberships, error } = await supabase
        .from('memberships')
        .select(`
          *,
          tenant:tenants(*)
        `)
        .eq('user_id', user.id);

      if (error) {
        console.error('[TenantContext] Error loading memberships:', error);
        throw error;
      }

      let membershipData = (memberships || []) as Membership[];

      // Only create default tenant if absolutely no memberships exist
      if (membershipData.length === 0) {
        // Super-admin bypass: super admins have no memberships and no tenant.
        // Recognize them here so ProtectedRoute doesn't misclassify them as
        // orphans needing onboarding. RLS on app_users allows self-select via
        // the `id = auth.uid()` policy branch, so this works without any
        // tenant/membership context.
        const { data: adminRow } = await supabase
          .from('app_users')
          .select('is_super_admin')
          .eq('id', user.id)
          .maybeSingle();

        if (adminRow?.is_super_admin) {
          setIsSuperAdmin(true);
          setUserMemberships([]);
          setCurrentTenant(null);
          setUserRole(null);
          setIsOrphanNeedingOnboarding(false);
          setHasOnlyPendingMemberships(false);
          setAllMembershipsSuspended(false);
          setLoading(false);
          return;
        }
        setIsSuperAdmin(false);

        
        // Double-check with another query to prevent race conditions
        // This prevents duplicate tenant creation if memberships were just created
        const { data: doubleCheck, error: doubleCheckError } = await supabase
          .from('memberships')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
          
        if (doubleCheckError) {
          console.error('[TenantContext] Double-check query failed:', doubleCheckError);
          throw doubleCheckError;
        }
        
        if (doubleCheck && doubleCheck.length > 0) {
          // Memberships exist now, reload the data
          setTimeout(() => loadTenantData(), 100);
          return;
        }
        
        // Check if user has active invitations - if so, wait for membership creation
        const hasInvite = await hasActiveInvitation(user.id);
        if (hasInvite) {
          console.log('[TenantContext] User has active invitation - waiting for membership creation');
          setUserMemberships([]);
          setCurrentTenant(null);
          setLoading(false);
          return;
        }
        
        // Check if this user was invited as a driver but doesn't have memberships yet
        // (This could indicate an incomplete driver setup)
        const { data: driverInvites } = await supabase
          .from('driver_invites')
          .select('tenant_id')
          .eq('user_id', user.id)
          .eq('status', 'created')
          .limit(1);
          
        if (driverInvites && driverInvites.length > 0) {
          console.warn('[TenantContext] User has driver invite but no membership - possible incomplete setup');
          // Don't create default tenant for invited drivers with incomplete setup
          setUserMemberships([]);
          setCurrentTenant(null);
          setLoading(false);
          return;
        }
        
        // Recover registrations created while the Auth provisioning trigger was
        // unavailable. Signup metadata is only present for the self-service
        // tenant-registration flow, so invited users remain unaffected.
        const signupMetadata = user.user_metadata as Record<string, unknown>;
        if (
          typeof signupMetadata.tenant_slug === 'string' && signupMetadata.tenant_slug.trim() &&
          typeof signupMetadata.company_name === 'string' && signupMetadata.company_name.trim()
        ) {
          const { error: recoveryError } = await (supabase.rpc as any)(
            'recover_pending_onboarding_from_auth_metadata',
          );

          if (!recoveryError) {
            console.info('[TenantContext] Recovered pending onboarding from signup metadata');
            setTimeout(() => loadTenantData(), 100);
            return;
          }

          console.error('[TenantContext] Could not recover pending onboarding:', recoveryError);
        }

        // Orphan check: no memberships, no invites — check for pending activation request
        const { data: activationRequests } = await supabase
          .from('tenant_activation_requests')
          .select('id, status')
          .eq('requesting_user_id', user.id)
          .in('status', ['pending', 'approved'])
          .limit(1);

        if (!activationRequests || activationRequests.length === 0) {
          // True orphan — needs to complete onboarding via AccountSetupScreen
          console.log('[TenantContext] User is orphan (no memberships, no activation request) — showing setup');
          setIsOrphanNeedingOnboarding(true);
          setUserMemberships([]);
          setCurrentTenant(null);
          setUserRole(null);
          setLoading(false);
          return;
        }

        // Has pending activation request but no membership yet — could be a race,
        // fall through to pending-membership treatment below via refetch.
        const { data: newMemberships, error: refetchError } = await supabase
          .from('memberships')
          .select(`
            *,
            tenant:tenants(*)
          `)
          .eq('user_id', user.id);

        if (refetchError) {
          console.error('[TenantContext] Error refetching memberships:', refetchError);
          throw refetchError;
        }

        membershipData = (newMemberships || []) as Membership[];
      }

      // Filter memberships: fully active = membership.status === 'active' AND tenant.status === 'active'
      const rawMemberships = membershipData;
      const activeMemberships = rawMemberships.filter(
        (m: any) => !m.suspended_at && m.status === 'active' && m.tenant?.status === 'active',
      );

      // If every membership is pending activation, gate the app
      const allPending =
        rawMemberships.length > 0 &&
        rawMemberships.every(
          (m: any) => m.status === 'pending_activation' || m.tenant?.status === 'pending_activation',
        );

      if (allPending) {
        setHasOnlyPendingMemberships(true);
        setAllMembershipsSuspended(false);
        setUserMemberships([]);
        setCurrentTenant(null);
        setUserRole(null);
        localStorage.removeItem('selectedTenantId');
        localStorage.removeItem('selectedTenantSlug');
        setLoading(false);
        return;
      }
      setHasOnlyPendingMemberships(false);
      setIsOrphanNeedingOnboarding(false);

      // If user has memberships but ALL are suspended, surface a clear blocked state
      if (rawMemberships.length > 0 && activeMemberships.length === 0) {
        setAllMembershipsSuspended(true);
        setUserMemberships([]);
        setCurrentTenant(null);
        setUserRole(null);
        // Clear any stale tenant selection
        localStorage.removeItem('selectedTenantId');
        localStorage.removeItem('selectedTenantSlug');
        setLoading(false);
        return;
      }
      setAllMembershipsSuspended(false);
      setUserMemberships(activeMemberships);

      // Determine current tenant
      let targetTenant: Tenant | null = null;

      // Get selected tenant from login or URL
      const tenantSlug = getSelectedTenant();

      if (tenantSlug) {
        // Try to find tenant by slug from user's ACTIVE memberships
        const membershipForSlug = activeMemberships.find(m => m.tenant.slug === tenantSlug);
        if (membershipForSlug) {
          targetTenant = membershipForSlug.tenant;
        } else {
          // User has no active membership in this tenant (either never had, or suspended)
          console.error(`[TenantContext] No active membership in tenant: ${tenantSlug}`);
          localStorage.removeItem('selectedTenantId');
          localStorage.removeItem('selectedTenantSlug');
        }
      }

      // Fallback to first available active tenant
      if (!targetTenant && activeMemberships.length > 0) {
        targetTenant = activeMemberships[0].tenant;
        localStorage.setItem('selectedTenantId', targetTenant.id);
        localStorage.setItem('selectedTenantSlug', targetTenant.slug);
      }

      setCurrentTenant(targetTenant);

      if (targetTenant) {
        const currentMembership = activeMemberships.find(m => m.tenant_id === targetTenant.id);
        setUserRole(currentMembership?.role || null);
      } else {
        setUserRole(null);
      }
    } catch (error) {
      console.error('[TenantContext] Error loading tenant data:', error);
      // Transient failure: preserve any previously-loaded tenant/memberships
      // so a network blip does not flash Access Denied. Real sign-out and
      // tenant-removal are handled by the `!user` branch and the
      // "no active memberships" branch above, so stale state cannot outlive
      // an actual identity change.
    } finally {
      setLoading(false);
    }

  };


  const refreshTenantData = async () => {
    setLoading(true);
    await loadTenantData();
  };

  useEffect(() => {
    loadTenantData();
    // Depend on the stable user id, not the user object identity. Supabase
    // may hand us a new user object on token refresh even when the actual
    // user is unchanged — reacting to that would remount the whole app.
  }, [user?.id]);

  // Build 2A — keep the proof-media Blob cache scoped to the current
  // identity. Any change to user or tenant invalidates every cached URL.
  useEffect(() => {
    if (!user?.id || !currentTenant?.id) {
      setProofMediaScope(null);
      invalidateAllProofMedia('sign-out');
      return;
    }
    setProofMediaScope({
      projectRef: getProofMediaProjectRef(),
      authUserId: user.id,
      tenantId: currentTenant.id,
    });
  }, [user?.id, currentTenant?.id]);

  return (
    <TenantContext.Provider value={{
      currentTenant,
      userMemberships,
      userRole,
      loading,
      allMembershipsSuspended,
      hasOnlyPendingMemberships,
      isOrphanNeedingOnboarding,
      isSuperAdmin,
      refreshTenantData,
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);